import { useEffect, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { X, MapPin, Clock, ArrowUpRight } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Remembers which announcement this visitor closed so it doesn't nag on every
// visit — a newly announced show (different id) pops up again.
const DISMISSED_KEY = "gig-popup-dismissed";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatDate = (iso) => {
  const d = new Date(iso + "T12:00:00");
  return {
    weekday: d.toLocaleString("en-US", { weekday: "long" }),
    month: d.toLocaleString("en-US", { month: "short" }),
    day: d.getDate().toString().padStart(2, "0"),
  };
};

export const GigAnnouncement = () => {
  const { data: shows = [] } = useQuery({
    queryKey: ["shows"],
    queryFn: async () => (await axios.get(`${API_BASE}/shows`)).data ?? [],
  });

  // Soonest announced show whose date hasn't passed — still shown on the day
  // of the gig, gone the day after.
  const gig = shows
    .filter((s) => s.announce && s.date && s.date >= todayISO())
    .sort((a, b) => (a.date < b.date ? -1 : 1))[0];

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!gig) return;
    let dismissed = null;
    try {
      dismissed = localStorage.getItem(DISMISSED_KEY);
    } catch {}
    if (dismissed === gig.id) return;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [gig?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem(DISMISSED_KEY, gig.id);
    } catch {}
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && dismiss();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!gig) return null;
  const d = formatDate(gig.date);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={dismiss}
          data-testid="gig-popup-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label="Show announcement"
            data-testid="gig-popup"
            className="relative bg-[#0A0A0A] border-2 border-white/10 max-w-md w-full max-h-[88vh] overflow-y-auto brutal-shadow"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1.5 bg-[#B91C1C]" />
            <button
              onClick={dismiss}
              aria-label="Close announcement"
              data-testid="gig-popup-close"
              className="absolute top-5 right-4 z-10 p-1 text-white/70 hover:text-white bg-black/50 transition-colors"
            >
              <X size={18} />
            </button>

            {gig.image_url && (
              <img
                src={gig.image_url}
                alt={`${gig.venue} show poster`}
                data-testid="gig-popup-poster"
                className="w-full max-h-72 object-cover object-top border-b-2 border-white/10"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            )}

            <div className="p-7 md:p-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-[#DC2626]" />
                <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
                  Catch Us Live
                </span>
              </div>

              <div className="flex items-start gap-5 mb-5">
                <div className="flex flex-col items-center border-2 border-white/10 px-4 py-2.5 shrink-0">
                  <span className="font-oswald uppercase tracking-[0.2em] text-xs text-[#DC2626]">
                    {d.month}
                  </span>
                  <span className="font-anton text-4xl text-white leading-none mt-0.5">
                    {d.day}
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="font-oswald uppercase tracking-wide text-white/60 text-xs mb-1">
                    {d.weekday}
                  </div>
                  <h3 className="font-anton uppercase text-white text-2xl md:text-3xl leading-[1.05]">
                    {gig.venue}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-white/60 text-sm mt-2 font-manrope">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={13} /> {gig.city}
                    </span>
                    {gig.time && (
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={13} /> {gig.time}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {gig.notes && (
                <p className="text-white/60 text-sm font-manrope mb-6">{gig.notes}</p>
              )}

              <div className="flex flex-wrap gap-3">
                {gig.ticket_url && (
                  <a
                    href={gig.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="gig-popup-tickets"
                    className="inline-flex items-center gap-2 font-oswald uppercase tracking-[0.2em] text-xs bg-[#B91C1C] hover:bg-[#991B1B] text-white px-5 py-3 transition-colors"
                  >
                    Get Tickets <ArrowUpRight size={14} />
                  </a>
                )}
                <a
                  href="#shows"
                  onClick={dismiss}
                  data-testid="gig-popup-all-shows"
                  className="inline-flex items-center gap-2 font-oswald uppercase tracking-[0.2em] text-xs text-white border border-white/30 hover:border-[#B91C1C] hover:bg-[#B91C1C] px-5 py-3 transition-all"
                >
                  All Shows
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
